/**
 * BoardsPage - EA-Agile Dual-Board View
 * Displays Sprint Board (current work) and Backlog Board (future work) side-by-side
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiGrid, FiList } from 'react-icons/fi';
import CardBoard from '../components/CardBoard';
import { useStatus } from '../contexts/StatusProvider';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

type BoardView = 'split' | 'sprint' | 'backlog';

interface Card {
  card_id: string;
  card_type: string;
  title: string;
  description?: string;
  state: string;
  priority: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Sprint board states
const SPRINT_STATES = ['todo', 'in_progress', 'in_review', 'done'];
// Backlog board states
const BACKLOG_STATES = ['backlog', 'ready', 'blocked'];

const BoardsPage: React.FC = () => {
  const { Client_ID, Auth_Status } = useStatus();
  const [viewMode, setViewMode] = useState<BoardView>('split');
  const [cards, setCards] = useState<Card[]>([]);

  // Get effective client ID (from StatusProvider or localStorage for super admin context)
  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId');

  // Fetch cards for stats calculation
  const fetchCards = useCallback(async () => {
    if (!Auth_Status || !effectiveClientId) return;

    try {
      const response = await Make_Authorized_API_Call<Card>(
        API_ENDPOINTS.EA_AGILE.CARDS,
        'GET',
        undefined,
        { params: { Client_ID: effectiveClientId } }
      );

      if (isSuccessResponse(response)) {
        setCards(response.data.Records || []);
      }
    } catch (err) {
      console.error('Error fetching cards for stats:', err);
    }
  }, [Auth_Status, effectiveClientId]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // Calculate stats from cards
  const stats = useMemo(() => {
    const sprintCards = cards.filter(c => SPRINT_STATES.includes(c.state));
    const backlogCards = cards.filter(c => BACKLOG_STATES.includes(c.state));
    const incidents = cards.filter(c => c.card_type === 'incident');
    const draftStandards = cards.filter(c => c.card_type === 'standard' && c.state === 'backlog');

    return {
      sprintCount: sprintCards.length,
      backlogCount: backlogCards.length,
      incidentCount: incidents.length,
      draftStandardsCount: draftStandards.length,
    };
  }, [cards]);

  return (
    <div className="p-6 h-full">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">EA-Agile Boards</h1>
            <p className="text-gray-600 mt-1">
              Manage incidents, standards, and implementation cards across Sprint and Backlog
            </p>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'split'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FiGrid className="inline mr-1" />
              Split View
            </button>
            <button
              onClick={() => setViewMode('sprint')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'sprint'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Sprint Only
            </button>
            <button
              onClick={() => setViewMode('backlog')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'backlog'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Backlog Only
            </button>
          </div>
        </div>

        {/* Board stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Sprint Cards</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.sprintCount}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Backlog Cards</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.backlogCount}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Incidents</div>
            <div className="text-2xl font-bold text-red-600 mt-1">{stats.incidentCount}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Draft Standards</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.draftStandardsCount}</div>
          </div>
        </div>
      </div>

      {/* Boards Container */}
      <div className={`grid ${viewMode === 'split' ? 'grid-cols-2' : 'grid-cols-1'} gap-6 h-[calc(100vh-280px)]`}>
        {/* Sprint Board */}
        {(viewMode === 'split' || viewMode === 'sprint') && (
          <div className="flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Sprint Board</h2>
                  <p className="text-sm text-blue-100">Current work in progress</p>
                </div>
                <div className="text-2xl font-bold">{stats.sprintCount}</div>
              </div>
            </div>
            <div className="flex-1 bg-white border-x border-b border-gray-200 rounded-b-lg overflow-hidden">
              <CardBoard
                boardType="sprint"
                clientId={effectiveClientId || ''}
              />
            </div>
          </div>
        )}

        {/* Backlog Board */}
        {(viewMode === 'split' || viewMode === 'backlog') && (
          <div className="flex flex-col">
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-4 py-3 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Backlog Board</h2>
                  <p className="text-sm text-gray-100">Future work & planning</p>
                </div>
                <div className="text-2xl font-bold">{stats.backlogCount}</div>
              </div>
            </div>
            <div className="flex-1 bg-white border-x border-b border-gray-200 rounded-b-lg overflow-hidden">
              <CardBoard
                boardType="backlog"
                clientId={effectiveClientId || ''}
              />
            </div>
          </div>
        )}
      </div>

      {/* Card type legend */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Card Types</h3>
        <div className="grid grid-cols-6 gap-3 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>Incident</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span>Problem</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Standard</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Feature</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span>Research</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span>Other</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardsPage;
